import { useSearchParams } from 'react-router-dom';
import { useState, useMemo } from 'react';

/**
 * Reads molecule/file context from URL search params.
 * Used by standalone workbench pages to receive context when launched
 * from the CompoundDetailPanel (via WorkbenchTab or DesignTab).
 */
export function useWorkbenchContext() {
  const [searchParams] = useSearchParams();

  return {
    smiles: searchParams.get('smiles') || undefined,
    moleculeId: searchParams.get('molecule_id') ? Number(searchParams.get('molecule_id')) : undefined,
    fileId: searchParams.get('file_id') ? Number(searchParams.get('file_id')) : undefined,
    projectId: searchParams.get('project_id') ? Number(searchParams.get('project_id')) : undefined,
    moleculeName: searchParams.get('molecule_name') || undefined,
    fileName: searchParams.get('file_name') || undefined,
    projectName: searchParams.get('project_name') || undefined,
  };
}

/**
 * Tracks tool step for the WorkbenchContextBar bail-out text.
 * Each workbench page calls setToolStep() as the user progresses.
 */
export function useToolStep() {
  const [toolStep, setToolStep] = useState(1);
  const [hasSaved, setHasSaved] = useState(false);

  const bailOutText = useMemo(() => {
    if (hasSaved) return 'Back to file';
    if (toolStep <= 3) return 'Back to file \u2014 nothing saved';
    if (toolStep === 4) return 'Back to file \u2014 results not saved';
    return 'Back to file';
  }, [toolStep, hasSaved]);

  return { toolStep, setToolStep, hasSaved, setHasSaved, bailOutText };
}

/**
 * Builds a URL-search-param string preserving molecule context,
 * so tool-switching carries over project/file/molecule info.
 */
export function buildContextParams(ctx: ReturnType<typeof useWorkbenchContext>): string {
  const p = new URLSearchParams();
  if (ctx.smiles) p.set('smiles', ctx.smiles);
  if (ctx.moleculeId) p.set('molecule_id', String(ctx.moleculeId));
  if (ctx.fileId) p.set('file_id', String(ctx.fileId));
  if (ctx.projectId) p.set('project_id', String(ctx.projectId));
  if (ctx.moleculeName) p.set('molecule_name', ctx.moleculeName);
  if (ctx.fileName) p.set('file_name', ctx.fileName);
  if (ctx.projectName) p.set('project_name', ctx.projectName);
  return p.toString();
}
